import { motion } from "framer-motion";
import { Compass, ScrollText, Network, ShieldCheck, Workflow, Building } from "lucide-react";

const PILLARS = [
  {
    Icon: ScrollText,
    title: "Policy Advisory",
    body: "Translating policy intent into implementable institutional architecture.",
  },
  {
    Icon: Compass,
    title: "Governance Modernization",
    body: "Re-engineering processes, structures and digital workflows inside the state.",
  },
  {
    Icon: Workflow,
    title: "Execution Intelligence",
    body: "Program management offices that move from sanction to outcome.",
  },
  {
    Icon: ShieldCheck,
    title: "Institutional Strategy",
    body: "Long-horizon planning for ministries, regulators and statutory bodies.",
  },
  {
    Icon: Network,
    title: "Public-Private Collaboration",
    body: "Structuring partnerships with capital, capability and accountability.",
  },
  {
    Icon: Building,
    title: "Transformation Management",
    body: "End-to-end transformation offices for nation-scale initiatives.",
  },
];

export const GovernanceStrategy = () => {
  return (
    <section
      id="governance"
      data-testid="governance-section"
      className="bg-ivory py-28 md:py-40 scroll-mt-nav"
    >
      <div className="container-bharat">
        <div className="max-w-4xl">
          <p className="overline">Governance & Strategy</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-navy mt-6 leading-[1.05] tracking-tight">
            Where <span className="italic text-gold">vision</span> meets
            execution.
          </h2>
          <p className="mt-8 text-graphite/70 text-base md:text-lg max-w-2xl leading-relaxed">
            Strategy without execution is theatre. We bring together policy
            depth, institutional empathy and transformation discipline — to
            ensure intent converts into measurable national outcomes.
          </p>
        </div>

        <div className="hairline my-14" />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-graphite/10">
          {PILLARS.map((p, i) => {
            const Icon = p.Icon;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                className="bg-ivory p-10 group hover:bg-white transition-colors duration-500"
                data-testid={`governance-pillar-${i}`}
              >
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-10 h-10 inline-flex items-center justify-center border border-navy/20 group-hover:border-gold transition-colors">
                    <Icon size={18} className="text-navy group-hover:text-gold transition-colors" />
                  </span>
                  <span className="overline text-graphite/40 text-[0.6rem]">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl text-navy leading-tight">
                  {p.title}
                </h3>
                <p className="mt-4 text-graphite/65 text-sm leading-relaxed">
                  {p.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default GovernanceStrategy;
